import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useProduct } from "@/context/ProductContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch, Plus, Trash, CheckCircle, WarningCircle, PencilSimple, ArrowRight, ArrowLeft, X, Link as LinkIcon, Globe, MapPin, Files } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPROVAL_TONE = {
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  in_review: "bg-amber-50 text-amber-700 border-amber-300",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};
const APPROVAL_LABEL = { draft: "Draft", in_review: "In review", approved: "Approved", rejected: "Rejected" };

function Pill({ tone = "slate", children }) {
  const map = {
    slate: "bg-slate-100 text-slate-600 border-slate-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
    amber: "bg-amber-50 text-amber-700 border-amber-300",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
  };
  return <span className={`inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${map[tone]}`}>{children}</span>;
}

function SectionCard({ section, view, onOpen }) {
  const missing = section.missing_content;
  const cc = section.citation_completeness_pct;
  const adaptCount = section.country_adaptations_required?.length || 0;
  return (
    <button
      data-testid={`section-card-${section.id}`}
      onClick={() => onOpen(section)}
      className="text-left bg-white border border-slate-200 hover:border-slate-900 transition-colors p-4 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="overline-label text-slate-400">{section.group}</p>
          <p className="font-heading font-bold text-sm tracking-tight mt-0.5">{section.label}</p>
        </div>
        <span className={`shrink-0 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${APPROVAL_TONE[section.approval_status]}`}>
          {APPROVAL_LABEL[section.approval_status]}
        </span>
      </div>
      <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-2">{section.requirement}</p>
      <div className="flex flex-wrap items-center gap-1.5 mt-1">
        {missing
          ? <Pill tone="red">Missing content</Pill>
          : <Pill tone="green">{section.best_evidence.length} best evidence</Pill>}
        {section.alternative_evidence.length > 0 && <Pill tone="blue">{section.alternative_evidence.length} alternative</Pill>}
        {view === "country_template" && adaptCount > 0 && <Pill tone="amber">{adaptCount} adaptation{adaptCount > 1 ? "s" : ""}</Pill>}
        {section.citation_total_count > 0 && (
          <Pill tone={cc === 100 ? "green" : cc >= 50 ? "amber" : "red"}>
            citations {cc}%
          </Pill>
        )}
        {section.reviewer_email && <Pill tone="slate">rev: {section.reviewer_email.split("@")[0]}</Pill>}
      </div>
    </button>
  );
}

function SectionDetail({ section, productId, assets, countries, view, onClose, onChange }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("evidence");
  const [linkKind, setLinkKind] = useState("best");
  const [linkPickIds, setLinkPickIds] = useState([]);
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState(section.notes || "");
  const [reviewer, setReviewer] = useState(section.reviewer_email || "");
  const [approval, setApproval] = useState(section.approval_status || "draft");
  const [adaptations, setAdaptations] = useState(section.country_adaptations_required || []);
  const [newAdapt, setNewAdapt] = useState({ country_id: "", requirement: "", note: "" });
  const [audit, setAudit] = useState([]);

  useEffect(() => { setNotes(section.notes || ""); setReviewer(section.reviewer_email || ""); setApproval(section.approval_status || "draft"); setAdaptations(section.country_adaptations_required || []); }, [section.id]);

  useEffect(() => {
    axios.get(`${API}/dossier-v2/audit`, { params: { product_id: productId, section_id: section.id } })
      .then((r) => setAudit(r.data || [])).catch(() => {});
  }, [section.id, productId, busy]);

  const linkedIds = useMemo(() => new Set([
    ...section.best_evidence.map((e) => e.asset_id),
    ...section.alternative_evidence.map((e) => e.asset_id),
  ]), [section]);

  const linkableAssets = assets.filter((a) => !linkedIds.has(a.id));

  const doLink = async () => {
    if (linkPickIds.length === 0) return toast.error("Select at least one asset");
    setBusy("link");
    try {
      await axios.post(
        `${API}/dossier-v2/sections/${section.id}/evidence?product_id=${productId}`,
        { asset_ids: linkPickIds, kind: linkKind });
      toast.success(`${linkPickIds.length} evidence linked as ${linkKind}`);
      setLinkPickIds([]); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Link failed"); }
    finally { setBusy(null); }
  };

  const unlink = async (asset_id) => {
    setBusy(`unlink-${asset_id}`);
    try {
      await axios.delete(
        `${API}/dossier-v2/sections/${section.id}/evidence/${asset_id}?product_id=${productId}`);
      toast.success("Evidence unlinked"); onChange();
    } catch (e) { toast.error("Unlink failed"); }
    finally { setBusy(null); }
  };

  const saveMeta = async () => {
    setBusy("save-meta");
    try {
      await axios.patch(
        `${API}/dossier-v2/sections/${section.id}?product_id=${productId}`,
        { notes, reviewer_email: reviewer, approval_status: approval });
      toast.success("Section saved"); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(null); }
  };

  const addAdaptation = async () => {
    if (!newAdapt.country_id) return toast.error("Country is required");
    const next = [...adaptations, newAdapt];
    setAdaptations(next);
    setNewAdapt({ country_id: "", requirement: "", note: "" });
    setBusy("adapt");
    try {
      await axios.patch(
        `${API}/dossier-v2/sections/${section.id}?product_id=${productId}`,
        { country_adaptations: next });
      toast.success("Country adaptation saved"); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); setAdaptations(adaptations); }
    finally { setBusy(null); }
  };

  const removeAdaptation = async (idx) => {
    const next = adaptations.filter((_, i) => i !== idx);
    setAdaptations(next);
    setBusy("adapt");
    try {
      await axios.patch(
        `${API}/dossier-v2/sections/${section.id}?product_id=${productId}`,
        { country_adaptations: next });
      onChange();
    } catch (e) { toast.error("Save failed"); setAdaptations(adaptations); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="section-detail" className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-slate-200 max-w-4xl w-full max-h-[90vh] flex flex-col">
        <header className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <p className="overline-label text-slate-400">{section.group}</p>
            <p className="font-heading font-bold text-base tracking-tight">{section.label}</p>
            <p className="text-[11px] text-slate-500 mt-1 max-w-2xl">{section.requirement}</p>
          </div>
          <button data-testid="section-close" onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1">
            <X size={16} />
          </button>
        </header>

        <div className="flex items-center gap-1 border-b border-slate-100 px-5">
          {[
            ["evidence", "Evidence"],
            ["adaptations", "Country adaptations"],
            ["review", "Review"],
            ["audit", "Audit"],
          ].map(([k, l]) => (
            <button key={k} data-testid={`section-tab-${k}`} onClick={() => setTab(k)}
              className={`text-[11px] font-semibold px-3 py-2.5 border-b-2 ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-700"}`}>
              {l}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {tab === "evidence" && (
            <>
              <div>
                <p className="overline-label text-slate-400">Best evidence (primary)</p>
                {section.best_evidence.length === 0 && (
                  <p data-testid="empty-best" className="text-[11px] text-slate-400 mt-1 border border-dashed border-slate-200 p-3">
                    Missing content. Link at least one best evidence to lift this section from &ldquo;missing&rdquo; state.
                  </p>
                )}
                <ul className="mt-1 space-y-1">
                  {section.best_evidence.map((e) => (
                    <li key={e.asset_id} data-testid={`best-item-${e.asset_id}`} className="flex items-center gap-2 border border-emerald-100 bg-emerald-50/40 px-2 py-1.5 text-[11px]">
                      <CheckCircle size={12} className="text-emerald-700 shrink-0" weight="fill" />
                      <span className="font-semibold text-slate-800 flex-1 truncate">{e.name}</span>
                      <Pill tone={e.citation_complete ? "green" : "amber"}>{e.citation_status?.replace(/_/g, " ")}</Pill>
                      <button data-testid={`unlink-${e.asset_id}`} disabled={busy === `unlink-${e.asset_id}`} onClick={() => unlink(e.asset_id)} className="text-slate-300 hover:text-red-600 px-1">
                        <Trash size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <p className="overline-label text-slate-400">Alternative evidence</p>
                {section.alternative_evidence.length === 0 && (
                  <p className="text-[11px] text-slate-400 mt-1">None yet.</p>
                )}
                <ul className="mt-1 space-y-1">
                  {section.alternative_evidence.map((e) => (
                    <li key={e.asset_id} data-testid={`alt-item-${e.asset_id}`} className="flex items-center gap-2 border border-blue-100 bg-blue-50/40 px-2 py-1.5 text-[11px]">
                      <span className="font-semibold text-slate-800 flex-1 truncate">{e.name}</span>
                      <Pill tone="slate">{e.type}</Pill>
                      <button data-testid={`unlink-${e.asset_id}`} disabled={busy === `unlink-${e.asset_id}`} onClick={() => unlink(e.asset_id)} className="text-slate-300 hover:text-red-600 px-1">
                        <Trash size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <p className="overline-label text-slate-400">Link evidence</p>
                <p className="text-[10px] text-slate-400 mt-0.5">The same evidence asset can appear in multiple sections — the register is single-source-of-truth.</p>
                <div className="flex items-center gap-2 mt-2">
                  <Select value={linkKind} onValueChange={setLinkKind}>
                    <SelectTrigger data-testid="link-kind" className="h-8 w-40 text-xs bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="best">Best evidence</SelectItem>
                      <SelectItem value="alternative">Alternative</SelectItem>
                    </SelectContent>
                  </Select>
                  <select data-testid="link-picker" multiple size={5}
                    value={linkPickIds} onChange={(e) => setLinkPickIds(Array.from(e.target.selectedOptions).map((o) => o.value))}
                    className="text-xs border border-slate-300 rounded p-1 flex-1 min-w-0">
                    {linkableAssets.map((a) => (
                      <option key={a.id} value={a.id}>{a.name} · {a.type}</option>
                    ))}
                  </select>
                  <button data-testid="link-submit" onClick={doLink} disabled={busy === "link"}
                    className="text-[11px] font-semibold text-white bg-slate-900 px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5">
                    {busy === "link" ? <CircleNotch size={11} className="animate-spin" /> : <LinkIcon size={11} />} Link
                  </button>
                </div>
              </div>
            </>
          )}

          {tab === "adaptations" && (
            <>
              <p className="overline-label text-slate-400">Required country adaptations</p>
              {adaptations.length === 0 && (
                <p data-testid="empty-adaptations" className="text-[11px] text-slate-400 border border-dashed border-slate-200 p-3">
                  No country-specific adaptations declared for this section.
                </p>
              )}
              <ul className="space-y-1">
                {adaptations.map((a, i) => (
                  <li key={i} data-testid={`adaptation-${i}`} className="flex items-start gap-2 border border-amber-100 bg-amber-50/40 px-2 py-1.5 text-[11px]">
                    <MapPin size={12} className="text-amber-700 shrink-0 mt-0.5" weight="fill" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800">{a.country_id}</p>
                      {a.requirement && <p className="text-slate-600">{a.requirement}</p>}
                      {a.note && <p className="text-slate-500 text-[10px] mt-0.5">{a.note}</p>}
                    </div>
                    <button data-testid={`remove-adaptation-${i}`} onClick={() => removeAdaptation(i)} className="text-slate-300 hover:text-red-600 px-1">
                      <Trash size={12} />
                    </button>
                  </li>
                ))}
              </ul>
              <div className="border-t border-slate-100 pt-3 space-y-2">
                <p className="overline-label text-slate-400">Add adaptation</p>
                <Select value={newAdapt.country_id || undefined}
                  onValueChange={(v) => setNewAdapt({ ...newAdapt, country_id: v })}>
                  <SelectTrigger data-testid="adapt-country" className="h-8 text-xs bg-white"><SelectValue placeholder="Choose country" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-60">
                    {countries.map((c) => <SelectItem key={c.id} value={c.id}>{c.flag} {c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input data-testid="adapt-requirement" className="h-8 text-xs" placeholder="Requirement (e.g. PMDA local sub-cohort)"
                  value={newAdapt.requirement} onChange={(e) => setNewAdapt({ ...newAdapt, requirement: e.target.value })} />
                <Textarea data-testid="adapt-note" rows={2} className="text-xs" placeholder="Adaptation note (optional)"
                  value={newAdapt.note} onChange={(e) => setNewAdapt({ ...newAdapt, note: e.target.value })} />
                <button data-testid="adapt-add" onClick={addAdaptation} disabled={busy === "adapt"}
                  className="text-[11px] font-semibold text-white bg-slate-900 px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5">
                  {busy === "adapt" ? <CircleNotch size={11} className="animate-spin" /> : <Plus size={11} />} Add adaptation
                </button>
              </div>
            </>
          )}

          {tab === "review" && (
            <>
              <div>
                <p className="overline-label text-slate-400">Reviewer</p>
                <Input data-testid="reviewer-input" className="h-8 text-xs mt-1"
                  placeholder="reviewer@example.com"
                  value={reviewer} onChange={(e) => setReviewer(e.target.value)} />
              </div>
              <div>
                <p className="overline-label text-slate-400">Approval status</p>
                <Select value={approval} onValueChange={setApproval}>
                  <SelectTrigger data-testid="approval-select" className="h-8 text-xs bg-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {["draft", "in_review", "approved", "rejected"].map((k) => (
                      <SelectItem key={k} value={k} disabled={!isAdmin && (k === "approved" || k === "rejected")}>
                        {APPROVAL_LABEL[k]}{(k === "approved" || k === "rejected") && !isAdmin ? " (admin only)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="overline-label text-slate-400">Notes</p>
                <Textarea data-testid="notes-input" rows={5} className="text-xs mt-1"
                  value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
              <button data-testid="save-meta" onClick={saveMeta} disabled={busy === "save-meta"}
                className="text-[11px] font-semibold text-white bg-slate-900 px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5">
                {busy === "save-meta" ? <CircleNotch size={11} className="animate-spin" /> : <PencilSimple size={11} />} Save
              </button>
            </>
          )}

          {tab === "audit" && (
            <>
              <p className="overline-label text-slate-400">Section history</p>
              {audit.length === 0 && <p className="text-[11px] text-slate-400">No changes yet.</p>}
              <ul className="space-y-1">
                {audit.map((a) => (
                  <li key={a.id} data-testid={`audit-${a.id}`} className="text-[11px] text-slate-600 border-l-2 border-slate-200 pl-2">
                    <b>{a.actor_email}</b> · <span className="text-slate-500">{a.action}</span>
                    <span className="text-slate-400"> · {new Date(a.at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function DossierWorkspace() {
  const { productId } = useProduct();
  const [view, setView] = useState("global_core"); // global_core | country_template | local_annexure
  const [countryId, setCountryId] = useState("");
  const [countries, setCountries] = useState([]);
  const [assets, setAssets] = useState([]);
  const [sections, setSections] = useState([]);
  const [openSection, setOpenSection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groupFilter, setGroupFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/intelligence/${productId}`).then((r) => {
      const cc = r.data?.countries || [];
      setCountries(cc);
      if (cc.length && !cc.some((c) => c.id === countryId)) setCountryId(cc[0].id);
    }).catch(() => {});
    axios.get(`${API}/evidence/${productId}`).then((r) => setAssets(r.data || [])).catch(() => {});
  }, [productId]);

  const load = useCallback(async () => {
    if (!productId) return;
    if ((view === "country_template" || view === "local_annexure") && !countryId) return;
    setLoading(true);
    try {
      let url = `${API}/dossier-v2/global-core?product_id=${productId}`;
      if (view === "country_template") url = `${API}/dossier-v2/country-template?product_id=${productId}&country_id=${countryId}`;
      if (view === "local_annexure")   url = `${API}/dossier-v2/local-annexure?product_id=${productId}&country_id=${countryId}`;
      const r = await axios.get(url);
      setSections(r.data.sections || []);
      if (openSection) {
        const fresh = (r.data.sections || []).find((s) => s.id === openSection.id);
        if (fresh) setOpenSection(fresh);
      }
    } catch (e) { toast.error("Failed to load dossier"); }
    finally { setLoading(false); }
  }, [productId, view, countryId, openSection?.id]);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => Array.from(new Set(sections.map((s) => s.group))), [sections]);
  const visible = groupFilter === "all" ? sections : sections.filter((s) => s.group === groupFilter);

  const stats = useMemo(() => ({
    total: sections.length,
    approved: sections.filter((s) => s.approval_status === "approved").length,
    missing: sections.filter((s) => s.missing_content).length,
    review: sections.filter((s) => s.approval_status === "in_review").length,
  }), [sections]);

  return (
    <div data-testid="dossier-workspace" className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        {[
          ["global_core", "Global Core", Globe],
          ["country_template", "Country Template", MapPin],
          ["local_annexure", "Local Annexure", Files],
        ].map(([k, l, Icon]) => (
          <button key={k} data-testid={`view-${k}`} onClick={() => setView(k)}
            className={`text-[11px] font-semibold px-3 py-2 border flex items-center gap-1.5 transition-colors ${view === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
            <Icon size={11} /> {l}
          </button>
        ))}
        {(view === "country_template" || view === "local_annexure") && (
          <Select value={countryId} onValueChange={setCountryId}>
            <SelectTrigger data-testid="country-picker" className="h-9 w-56 text-xs bg-white"><SelectValue placeholder="Select country" /></SelectTrigger>
            <SelectContent className="bg-white max-h-60">
              {countries.map((c) => <SelectItem key={c.id} value={c.id}>{c.flag} {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div data-testid="dossier-stats" className="grid grid-cols-4 bg-white border border-slate-200 divide-x divide-slate-100">
        {[
          ["Sections", stats.total, "slate"],
          ["Approved", stats.approved, "green"],
          ["In review", stats.review, "amber"],
          ["Missing content", stats.missing, "red"],
        ].map(([l, v, tone]) => (
          <div key={l} className="px-4 py-3">
            <p className="overline-label text-slate-400">{l}</p>
            <p className={`font-heading font-extrabold text-2xl mt-0.5 ${tone === "red" && v > 0 ? "text-red-600" : tone === "green" ? "text-emerald-700" : tone === "amber" && v > 0 ? "text-amber-600" : ""}`}>{v}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {["all", ...groups].map((g) => (
          <button key={g} data-testid={`group-filter-${g}`} onClick={() => setGroupFilter(g)}
            className={`text-[10px] font-semibold px-2.5 py-1 border ${groupFilter === g ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
            {g === "all" ? "All groups" : g}
          </button>
        ))}
      </div>

      {loading ? (
        <div data-testid="loading-workspace" className="py-20 text-center text-xs text-slate-400">Loading dossier workspace…</div>
      ) : visible.length === 0 ? (
        <div data-testid="empty-workspace" className="py-20 text-center text-xs text-slate-400 border border-dashed border-slate-200">
          {view === "local_annexure"
            ? "No local annexure content yet — add country adaptations to sections to populate this view."
            : "No sections match the current filter."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {visible.map((s) => (
            <SectionCard key={s.id} section={s} view={view} onOpen={setOpenSection} />
          ))}
        </div>
      )}

      {openSection && (
        <SectionDetail
          section={openSection}
          productId={productId}
          assets={assets}
          countries={countries}
          view={view}
          onClose={() => setOpenSection(null)}
          onChange={load}
        />
      )}
    </div>
  );
}
